/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import ISO06391 from 'iso-639-1';
import { defineMessages, injectIntl, FormattedMessage } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';
import { formatProgramType } from '@utils/edxProductDisplay';
import { getTopicUrl } from '@utils/topics';
import ActiveProgramShape from '@prospectus/common/types/activeProgramShape';

function AtAGlance({
  intl,
  programs,
  owners,
  subjects: [primarySubject],
  levelType,
  prerequisitesRaw,
  skillNames,
  activeCourseRun: {
    contentLanguage,
    transcriptLanguages,
  },
}) {
  const messages = defineMessages({
    'prospectus.course.detail.at.a.glance.title': {
      id: 'prospectus.course.detail.at.a.glance.title',
      description: 'Heading for the At a Glance section of the course detail page',
      defaultMessage: 'At a glance',
    },
    'prospectus.course.detail.at.a.glance.subject': {
      id: 'prospectus.course.detail.at.a.glance.subject',
      description: 'Label for the course subject, eg \'Engineering\'',
      defaultMessage: 'Subject: ',
    },
    'prospectus.course.detail.at.a.glance.level': {
      id: 'prospectus.course.detail.at.a.glance.level',
      description: 'Label for the course level, eg \'Introductory\'',
      defaultMessage: 'Level: ',
    },
    'prospectus.course.detail.at.a.glance.prerequisite': {
      id: 'prospectus.course.detail.at.a.glance.prerequisite',
      description: 'Label for the course prerequisites in the At a Glance section',
      defaultMessage: 'Prerequisites: ',
    },
    'prospectus.course.detail.at.a.glance.language': {
      id: 'prospectus.course.detail.at.a.glance.language',
      description: 'Label for the course language , eg \'English\'',
      defaultMessage: 'Language: ',
    },
    'prospectus.course.detail.at.a.glance.associated_programs': {
      id: 'prospectus.course.detail.at.a.glance.associated_programs',
      description: 'Label for the course\'s associated programs in the At a Glance section',
      defaultMessage: 'Associated programs:',
    },
    'prospectus.course.detail.at.a.glance.none': {
      id: 'prospectus.course.detail.at.a.glance.none',
      description: 'Text indicating there are no prerequisites for a course',
      defaultMessage: 'None',
    },
    'prospectus.course.detail.at.a.glance.associated_skills': {
      id: 'prospectus.course.detail.at.a.glance.associated_skills',
      description: 'Label for the course\'s associated skills in the At a Glance section, eg \'Javascript\'',
      defaultMessage: 'Associated skills: ',
    },
  });

  const numOwners = owners.length;

  /**
  * Sometimes space comes with translation, sometimes not. Sometimes it gets
  * collapsed, because after intl module update, formatMessage has no \<span\>
  * wrapper. It's better to use breaking space (more responsitive).
  */
  const uncollapsedSpace = ' ';

  /**
   * Making skills a single string gives us more control over the layout
   * because we can count it's length by characters instead of amount of skills
   * which can vary in length.
   */
  const skills = skillNames.join(', ');
  const skillCharactersBreakPoint = 200;

  /**
   * Video transcript section for reuse
   */

  const transcriptLanguageJSX = (
    <li>
      <span className="font-weight-bold">
        <FormattedMessage
          id="prospectus.legacy-course.sidebar.video-transcript"
          defaultMessage="{numLanguages, plural, one {Video Transcript: } other {Video Transcripts: }}"
          description="The strings that should be translated are 'Video Transcript:' and 'Video Transcripts:' They will be followed by a list of languages supported in video transcripts."
          values={{
            numLanguages: transcriptLanguages.length,
          }}
        />
      </span>
      {uncollapsedSpace}
      {transcriptLanguages.map(language => (
        ISO06391.getNativeName(language.slice(0, 2))
      )).join(', ')}
    </li>
  );

  /**
   * Language section for reuse
   */
  let contentLanguageJSX = null;
  if (contentLanguage) {
    contentLanguageJSX = (
      <li>
        <span className="font-weight-bold">
          {intl.formatMessage(messages['prospectus.course.detail.at.a.glance.language'])}
        </span>
        {uncollapsedSpace}{ISO06391.getNativeName(contentLanguage.slice(0, 2))}
      </li>
    );
  }

  /**
   * Programs section for reuse
   */
  const programsJSX = (
    <li>
      <span className="font-weight-bold">
        {intl.formatMessage(messages['prospectus.course.detail.at.a.glance.associated_programs'])}
      </span>
      <ul className="list-unstyled mb-0">
        {programs.map((program) => (
          <li key={program.uuid}>
            <FormattedMessage
              id="prospectus.course.detail.at.a.glance.program"
              defaultMessage="{programType} in {programTitle}"
              description="Description of a program, eg 'Micromasters in Data Science Analytics'"
              values={{
                programType: formatProgramType(program.type),
                programTitle: (
                  <ProspectusLink
                    href={program.program.marketingPath}
                    analytics={{
                      eventName: 'edx.bi.link.course-sidebar.program.click',
                      linkProps: {
                        label: `${program.title} [${program.uuid}]`,
                      },
                    }}
                    className="muted-link inline-link text-wrap"
                  >{program.title}
                  </ProspectusLink>
                ),
              }}
            />
          </li>
        ))}
      </ul>
    </li>
  );
  return (
    <div className="pt-4 mt-sm-3 pt-sm-3">
      <h3 className="text-black pb-sm-2">{intl.formatMessage(messages['prospectus.course.detail.at.a.glance.title'])}</h3>
      <div className="at-a-glance">
        <div className="row">
          <div className="col-12 col-md-6">
            <ul className="mb-0 pl-3 ml-1">
              <li>
                <span className="font-weight-bold">
                  <FormattedMessage
                    id="prospectus.course.detail.at.a.glance.institution"
                    defaultMessage="{numOwners, plural, one {Institution:} other {Institutions:}}"
                    description="We will want 'Institution' and 'Institutions' translated."
                    values={{
                      numOwners,
                    }}
                  />
                </span>
                {uncollapsedSpace}
                {owners.map((owner) => (
                  !owner?.tags?.includes('build-as-preview') && owner?.tags?.includes('displayed_on_schools_and_partners_page') && owner?.marketingUrl
                    ? (
                      <ProspectusLink
                        href={`school/${owner.slug.toLowerCase()}`}
                        key={owner.marketingUrl}
                        analytics={{
                          eventName: 'edx.bi.course.partner.marketinglinks.clicked',
                        }}
                        className="muted-link inline-link"
                      >
                        {owner.key}
                      </ProspectusLink>
                    ) : (
                      <p className="d-inline">{owner.key}</p>
                    )
                ))}
              </li>
              <li>
                <span className="font-weight-bold">
                  {intl.formatMessage(messages['prospectus.course.detail.at.a.glance.subject'])}
                </span>
                {uncollapsedSpace}
                <ProspectusLink
                  href={getTopicUrl(intl.locale, primarySubject.slug)}
                  analytics={{
                    eventName: 'edx.bi.course.sidebar.subject.clicked',
                  }}
                  className="muted-link inline-link"
                >
                  {primarySubject.subject.labels[intl.locale]}
                </ProspectusLink>
              </li>
              <li>
                <span className="font-weight-bold">
                  {intl.formatMessage(messages['prospectus.course.detail.at.a.glance.level'])}
                </span>
                {uncollapsedSpace}{levelType}
              </li>
              <li>
                <span className="font-weight-bold">
                  {intl.formatMessage(messages['prospectus.course.detail.at.a.glance.prerequisite'])}
                </span>
                {uncollapsedSpace}
                <div
                  dangerouslySetInnerHTML={{ __html: prerequisitesRaw && prerequisitesRaw.length > 0 ? prerequisitesRaw : intl.formatMessage(messages['prospectus.course.detail.at.a.glance.none']) }}
                />
              </li>
              {programs.length && skills.length > skillCharactersBreakPoint ? programsJSX : null}
              {programs.length >= 3 && contentLanguage ? contentLanguageJSX : null}
              {transcriptLanguages && programs.length >= 3 ? transcriptLanguageJSX : null}
            </ul>
          </div>
          <div className="col-12 col-md-6">
            <ul className="pl-3 ml-1 mb-0">
              {programs.length < 3 && contentLanguage ? contentLanguageJSX : null}
              {transcriptLanguages && programs.length < 3 ? transcriptLanguageJSX : null}
              {programs.length && skills.length <= skillCharactersBreakPoint ? programsJSX : null}
              {skills.length ? (
                <li>
                  <span className="font-weight-bold">
                    {intl.formatMessage(messages['prospectus.course.detail.at.a.glance.associated_skills'])}
                  </span>
                  <span>{skills}</span>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

AtAGlance.propTypes = {
  intl: intlShape.isRequired,
  programs: PropTypes.arrayOf(PropTypes.shape(ActiveProgramShape)),
  skillNames: PropTypes.arrayOf(PropTypes.string),
  prerequisitesRaw: PropTypes.string,
  owners: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string,
    marketingUrl: PropTypes.string,
    key: PropTypes.string,
  })).isRequired,
  subjects: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string,
    labels: PropTypes.shape({}),
  })).isRequired,
  activeCourseRun: PropTypes.shape({
    contentLanguage: PropTypes.string,
    transcriptLanguages: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  levelType: PropTypes.string.isRequired,
};

AtAGlance.defaultProps = {
  programs: [],
  skillNames: [],
  prerequisitesRaw: null,
};

export default track({
  component: 'course-at-a-glance',
})(injectIntl(AtAGlance));
